import { getLocalizedField } from '../../../utils/localizedFields';
import { buildMediaUrl } from '../../../utils/urlHelpers';


const DAY_KEYS = Object.freeze([
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
]);

const asArray = (value) => (Array.isArray(value) ? value : []);
const relationKey = (value) => (value == null ? null : String(value));
const isRecord = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const parseOptionalNumber = (value) => {
  if (value == null) return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isRawId = (value) => (
  (typeof value === 'number' && Number.isFinite(value))
  || (typeof value === 'string' && Boolean(value.trim()))
);

const getId = (value, ...keys) => {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    if (isRawId(value[key])) return value[key];
  }
  return null;
};

const safeMediaUrl = (candidate) => {
  try {
    return buildMediaUrl(candidate);
  } catch (_error) {
    return '';
  }
};

const getPhotoCandidate = (photo) => {
  if (typeof photo === 'string') return photo;
  if (!photo || typeof photo !== 'object') return '';
  return photo.url || photo.image || photo.photo || photo.file || photo.path || photo.src || '';
};

const normalizePhotos = (salon, salonName) => {
  if (!salon || typeof salon !== 'object') return [];

  const candidates = [
    ...asArray(salon.photos),
    salon.cover_photo,
    salon.photo,
    salon.image,
  ];
  const seen = new Set();

  return candidates.reduce((photos, photo) => {
    const src = safeMediaUrl(getPhotoCandidate(photo));
    if (!src || seen.has(src)) return photos;
    seen.add(src);

    photos.push({
      id: getId(photo, 'photo_id', 'id'),
      src,
      alt: [photo?.alt, photo?.caption, photo?.name, photo?.title]
        .find((value) => typeof value === 'string' && value.trim())
        || salonName,
      isPrimary: Boolean(photo?.is_primary),
      raw: photo,
    });
    return photos;
  }, []);
};

const promotionAppliesToService = (promotion, service) => {
  const eligibility = promotion?.eligibility;
  if (!eligibility) return false;
  if (eligibility.apply_to_all_services) return true;

  const serviceId = getId(service, 'service_id', 'id');
  const categoryId = service?.category ?? service?.category_id ?? null;
  return (
    (serviceId != null && asArray(eligibility.services).includes(serviceId))
    || (categoryId != null && asArray(eligibility.categories).includes(categoryId))
  );
};

const buildPromotionByServiceId = (services, promotions) => {
  const promotionByServiceId = {};

  asArray(services).forEach((service) => {
    if (!isRecord(service)) return;
    const serviceId = getId(service, 'service_id', 'id');
    const basePrice = parseOptionalNumber(service.price);
    if (serviceId == null || basePrice == null) return;

    let bestPromotion = null;
    asArray(promotions).forEach((promotion) => {
      if (!isRecord(promotion)) return;
      const promotionId = getId(promotion, 'promotion_id', 'id');
      const declaresId = hasOwn(promotion, 'promotion_id') || hasOwn(promotion, 'id');
      if (declaresId && promotionId == null) return;
      if (promotion?.rule?.rule_type !== 'percent_off') return;
      if (!promotionAppliesToService(promotion, service)) return;

      const percent = parseOptionalNumber(promotion?.rule?.params?.percent);
      if (percent == null || percent <= 0) return;
      if (!bestPromotion || percent > bestPromotion.percent) {
        bestPromotion = {
          id: promotionId,
          percent,
          discountedPrice: Math.max(0, basePrice * (1 - percent / 100)).toFixed(0),
          raw: promotion,
        };
      }
    });

    if (bestPromotion) promotionByServiceId[serviceId] = bestPromotion;
  });

  return promotionByServiceId;
};

const normalizeService = (service, language, canClientBook, promotionByServiceId) => {
  const id = getId(service, 'service_id', 'id');
  const hasAssignedEmployees = typeof service?.has_assigned_employees === 'boolean'
    ? service.has_assigned_employees
    : true;
  const isClientBookingDisabled = (
    service?.client_booking_disabled === true
    || service?.client_booking_disabled === 'true'
  );

  return {
    id,
    categoryId: service?.category ?? service?.category_id ?? null,
    name: getLocalizedField(service, 'name', language),
    description: getLocalizedField(service, 'description', language),
    price: parseOptionalNumber(service?.price),
    duration: service?.duration ?? null,
    formattedDuration: service?.formatted_duration || '',
    isFixedPrice: service?.prix_fixe !== false,
    hasAssignedEmployees,
    canBook: Boolean(canClientBook && hasAssignedEmployees && !isClientBookingDisabled),
    bookingConfirmationMode: service?.booking_confirmation_mode || null,
    promotion: promotionByServiceId[id] || null,
    raw: service,
  };
};

const normalizeServiceCategories = (
  categories,
  services,
  language,
  canClientBook,
  promotionByServiceId,
) => {
  const normalizedCategories = asArray(categories).reduce((items, category) => {
    if (!isRecord(category)) return items;
    const id = getId(category, 'category_id', 'id');
    if (id == null) return items;
    items.push({
      id,
      name: getLocalizedField(category, 'name', language),
      services: [],
      raw: category,
    });
    return items;
  }, []);
  const categoryById = new Map(normalizedCategories.map(
    (category) => [relationKey(category.id), category],
  ));
  const uncategorizedServices = [];

  asArray(services).forEach((service) => {
    if (!isRecord(service) || getId(service, 'service_id', 'id') == null) return;
    const normalized = normalizeService(
      service,
      language,
      canClientBook,
      promotionByServiceId,
    );
    const category = categoryById.get(relationKey(normalized.categoryId));
    if (category) category.services.push(normalized);
    else uncategorizedServices.push(normalized);
  });

  if (uncategorizedServices.length > 0) {
    normalizedCategories.push({
      id: 'uncategorized',
      name: '',
      services: uncategorizedServices,
      raw: null,
    });
  }

  return normalizedCategories;
};

const normalizeTeam = (
  team,
  normalizedServices,
  language,
  canClientBook,
  promotionByServiceId,
) => {
  const servicesById = new Map(normalizedServices.map((service) => [service.id, service]));

  return asArray(team).filter((member) => (
    isRecord(member) && getId(member, 'team_member_id', 'user_id', 'id') != null
  )).map((member) => {
    const firstName = member?.first_name || '';
    const lastName = member?.last_name || '';
    return {
      id: getId(member, 'team_member_id', 'user_id', 'id'),
      userId: getId(member, 'user_id'),
      firstName,
      lastName,
      name: `${firstName} ${lastName}`.trim(),
      photo: safeMediaUrl(member?.profile_photo),
      services: asArray(member?.services).filter((service) => (
        isRecord(service) && getId(service, 'service_id', 'id') != null
      )).map((service) => {
        const serviceId = getId(service, 'service_id', 'id');
        return servicesById.get(serviceId) || normalizeService(
          service,
          language,
          canClientBook,
          promotionByServiceId,
        );
      }),
      raw: member,
    };
  });
};

const normalizeReviews = (reviews) => asArray(reviews).filter((review) => (
  isRecord(review) && getId(review, 'review_id', 'id') != null
)).map((review) => ({
  id: getId(review, 'review_id', 'id'),
  authorName: review?.client_name || review?.user_name || review?.author || '',
  rating: parseOptionalNumber(review?.rating),
  comment: review?.comment || review?.content || '',
  createdAt: review?.created_at || null,
  raw: review,
}));

const parseOpeningHours = (openingHours) => {
  if (isRecord(openingHours)) {
    return openingHours;
  }
  if (typeof openingHours !== 'string' || !openingHours.trim()) return null;
  try {
    const parsed = JSON.parse(openingHours);
    return isRecord(parsed) ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const getDayHours = (raw) => {
  if (!isRecord(raw)) return null;
  const open = raw.ouverture ?? raw.open ?? raw.opening ?? raw.start;
  const close = raw.fermeture ?? raw.close ?? raw.closing ?? raw.end;
  const isHourValue = (value) => (
    (typeof value === 'string' && Boolean(value.trim()))
    || (typeof value === 'number' && Number.isFinite(value))
  );
  const hasOpen = isHourValue(open);
  const hasClose = isHourValue(close);
  return hasOpen && hasClose ? { open, close } : null;
};

const normalizeOpeningHours = (openingHours) => {
  const hours = parseOpeningHours(openingHours);
  if (!hours || !DAY_KEYS.some((day) => getDayHours(hours[day]))) return [];

  return DAY_KEYS.map((day) => {
    const raw = hours[day];
    const dayHours = getDayHours(raw);
    const openValue = dayHours?.open ?? '';
    const closeValue = dayHours?.close ?? '';
    const isClosed = (
      !openValue
      || !closeValue
      || String(openValue).toLowerCase() === 'fermé'
      || String(closeValue).toLowerCase() === 'fermé'
    );
    return {
      day,
      open: isClosed ? '' : String(openValue).slice(0, 5),
      close: isClosed ? '' : String(closeValue).slice(0, 5),
      isClosed,
      raw: raw || null,
    };
  });
};

const normalizeClosures = (closures) => {
  const now = new Date();
  return asArray(closures).filter((closure) => {
    if (!isRecord(closure)) return false;
    const declaresId = hasOwn(closure, 'closure_id') || hasOwn(closure, 'id');
    if (declaresId && getId(closure, 'closure_id', 'id') == null) return false;
    const hasClosureData = [
      'start_datetime',
      'end_datetime',
      'reason',
      'status',
      'is_recurring',
      'month',
      'day',
    ].some((key) => hasOwn(closure, key));
    if (!declaresId && !hasClosureData) return false;
    if ((closure.status || 'active') !== 'active') return false;
    if (closure.is_recurring || !closure.end_datetime) return true;

    const end = new Date(closure.end_datetime);
    return Number.isNaN(end.getTime()) || end >= now;
  }).map((closure) => ({
    id: getId(closure, 'closure_id', 'id'),
    startAt: closure.start_datetime || null,
    endAt: closure.end_datetime || null,
    reason: closure.reason ? String(closure.reason) : '',
    status: closure.status || 'active',
    isRecurring: Boolean(closure.is_recurring),
    month: closure.month ?? null,
    day: closure.day ?? null,
    raw: closure,
  }));
};

export const buildSalonTemplateViewModel = ({
  salon,
  categories,
  services,
  promotions,
  team,
  reviews,
  openingHours,
  closures,
  language,
  canClientBook,
  isFavorite,
} = {}) => {
  const rawSalon = salon && typeof salon === 'object' ? salon : null;
  const name = getLocalizedField(rawSalon, 'name', language);
  const promotionByServiceId = buildPromotionByServiceId(services, promotions);
  const serviceCategories = normalizeServiceCategories(
    categories,
    services,
    language,
    canClientBook,
    promotionByServiceId,
  );
  const normalizedServices = serviceCategories.flatMap((category) => category.services);
  const normalizedReviews = normalizeReviews(reviews);
  const ratedReviews = normalizedReviews.filter((review) => review.rating != null);

  return {
    identity: {
      id: getId(rawSalon, 'salon_id', 'id'),
      name,
      description: getLocalizedField(rawSalon, 'description', language),
      phone: rawSalon?.phone || '',
      email: rawSalon?.email || '',
      address: getLocalizedField(rawSalon, 'address', language)
        || getLocalizedField(rawSalon, 'formatted_address', language)
        || rawSalon?.formattedAddress
        || '',
      latitude: rawSalon?.latitude ?? null,
      longitude: rawSalon?.longitude ?? null,
      raw: rawSalon,
    },
    photos: normalizePhotos(rawSalon, name),
    serviceCategories,
    promotionsByServiceId: promotionByServiceId,
    team: normalizeTeam(
      team,
      normalizedServices,
      language,
      canClientBook,
      promotionByServiceId,
    ),
    reviews: normalizedReviews,
    averageRating: ratedReviews.length > 0
      ? ratedReviews.reduce((sum, review) => sum + review.rating, 0) / ratedReviews.length
      : null,
    openingHours: normalizeOpeningHours(openingHours),
    closures: normalizeClosures(closures),
    capabilities: {
      canClientBook: Boolean(canClientBook),
      isFavorite: Boolean(isFavorite),
    },
  };
};

export default buildSalonTemplateViewModel;
